<script setup lang="ts">
import { useGetEmployee } from "@/api/employees";
import { type CurrentIn } from "@/api/employees/types";
import type { Location } from "@/api/locations";
import type { MovementWithFields } from "@/api/movements";
import type { VisitWithFields } from "@/api/visits";
import { useDateUtils } from "@/composables/dates";
import { useLocationSelector } from "@/composables/location";
import { STATUSES, USER_TYPE } from "@/stores/constants";
import { formatFullName } from "@/utils/string";
import dayjs from "dayjs";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

type Props = {
  movement: CurrentIn | VisitWithFields | MovementWithFields;
  location?: Partial<Location>;
  type: "employee" | "visitor" | "movement";
};

const props = defineProps<Props>();

const { t } = useI18n();
const { formatDateTime, formatMovementDate } = useDateUtils();
const { selectedLocation } = useLocationSelector();

const id = computed(() => {
  switch (props.type) {
    case USER_TYPE.VISITOR:
      return (props.movement as VisitWithFields).reviewerId;
    case USER_TYPE.EMPLOYEE:
      return (props.movement as CurrentIn).reviewedBy;
    default:
      return (props.movement as MovementWithFields).reviewerId;
  }
});

const { data: employeeData } = useGetEmployee(
  computed(() => (id.value ? String(id.value) : undefined))
);

const reviewerFullName = computed(() =>
  formatFullName(employeeData.value?.firstName, employeeData.value?.lastName)
);

const reviewStatus = computed(() => {
  switch (props.type) {
    case USER_TYPE.VISITOR:
      return (props.movement as VisitWithFields).reviewStatus;
    case USER_TYPE.EMPLOYEE:
      return (props.movement as CurrentIn).reviewStatus;
    default:
      return (props.movement as MovementWithFields).reviewStatus;
  }
});

const showApprovedByAt = computed(
  () => reviewStatus.value === STATUSES.STATUS_ALLOW && reviewerFullName.value
);

const inTime = computed(() => {
  switch (props.type) {
    case USER_TYPE.VISITOR:
      return (props.movement as VisitWithFields).startedAt;
    case USER_TYPE.EMPLOYEE:
      return (props.movement as CurrentIn).startedAt;
    default:
      return (props.movement as MovementWithFields).startedAt;
  }
});

const outTime = computed(() => {
  let current;

  switch (props.type) {
    case USER_TYPE.VISITOR:
      return (props.movement as VisitWithFields).finishedAt;
    case USER_TYPE.EMPLOYEE:
      current = props.movement as CurrentIn;

      return current.status?.id === STATUSES.STATUS_IN_ID
        ? current.finishedAt
        : current.startedAt;
    default:
      return (props.movement as MovementWithFields).finishedAt;
  }
});

const reviewedAt = computed(() => {
  switch (props.type) {
    case USER_TYPE.VISITOR:
      return (
        (props.movement as VisitWithFields).reviewedAt?.toISOString() ?? ""
      );
    case USER_TYPE.EMPLOYEE:
      return (props.movement as CurrentIn).reviewedAt ?? "";
    default:
      return (
        (props.movement as MovementWithFields).reviewedAt?.toISOString() ?? ""
      );
  }
});

const dateTime = computed(() => {
  const visitReviewedAtTZ = (props.movement as VisitWithFields)
    .reviewedAt as Date;
  const movementReviewedAtTZ = (props.movement as MovementWithFields)
    .reviewedAt as Date;

  switch (props.type) {
    case USER_TYPE.VISITOR:
      return formatDateTime(visitReviewedAtTZ);
    case USER_TYPE.EMPLOYEE:
      return formatDateTime(
        dayjs(reviewedAt.value)
          .tz(selectedLocation.value?.timeZone)
          .toString()
      );
    default:
      return formatDateTime(movementReviewedAtTZ);
  }
});

const descriptionStatusDeny = computed(() =>
  reviewerFullName.value
    ? t("timelines.deniedByAt", {
        name: reviewerFullName.value,
        dateTime: dateTime.value,
      })
    : t("timelines.deniedAt", { dateTime: dateTime.value })
);

const descriptionStatusApprove = computed(() => {
  let datetime: Date | string;

  switch (props.type) {
    case USER_TYPE.VISITOR:
      datetime = formatDateTime(
        (props.movement as VisitWithFields).reviewedAt ?? ""
      );
      break;
    case USER_TYPE.EMPLOYEE:
      datetime = formatMovementDate(
        reviewedAt.value ?? "",
        selectedLocation.value.timeZone
      );
      break;
    default:
      datetime = formatDateTime(
        (props.movement as MovementWithFields).reviewedAt ?? ""
      );
  }

  return reviewerFullName.value
    ? t("timelines.approvedByAt", {
        name: reviewerFullName.value,
        dateTime: datetime,
      })
    : t("timelines.approvedAt", { dateTime: dateTime });
});
</script>

<template>
  <div class="break--line-word">
    <p v-if="reviewStatus === STATUSES.STATUS_PENDING && inTime">
      {{
        t("timelines.pendingAt", {
          dateTime: formatDateTime(inTime),
        })
      }}
    </p>

    <p v-else-if="reviewStatus === STATUSES.STATUS_DENY">
      {{ descriptionStatusDeny }}
    </p>

    <p v-else-if="reviewStatus === STATUSES.STATUS_LAPSED && outTime">
      {{ t("timelines.deniedAt", { dateTime: formatDateTime(outTime) }) }}
    </p>

    <p v-else-if="inTime && outTime">
      {{
        t("timelines.signedOutAt", {
          dateTime: formatDateTime(outTime),
        })
      }}
    </p>

    <p v-else>
      {{
        t("timelines.signedInAt", {
          dateTime: formatDateTime(inTime ?? ""),
        })
      }}
    </p>

    <p v-if="showApprovedByAt">
      {{ descriptionStatusApprove }}
    </p>
  </div>
</template>
